import { useCallback, useContext, useEffect } from 'react';
import { UNSAFE_NavigationContext as NavigationContext } from 'react-router';

import { useConfirmDialog } from 'contexts/ConfirmDialog';
import type { IGenericObject } from 'types';

const useConfirmExit = (
  confirmExit: (confirmNavigation: () => void) => void,
  when = true,
  baseExcludedPath = '',
) => {
  const { navigator } = useContext(NavigationContext);

  useEffect(() => {
    if (!when) return;

    const push = navigator.push;

    navigator.push = (...args: Parameters<typeof push>) => {
      const { pathname } = args[0] as IGenericObject;
      const confirm = () => push(...args);

      if (pathname.startsWith(baseExcludedPath)) {
        confirm();
      } else {
        confirmExit(confirm);
      }
    };

    return () => {
      navigator.push = push;
    };
  }, [navigator, confirmExit, when, baseExcludedPath]);
};

export const useCustomPrompt = (
  { title, message, onActionLabel, baseExcludedPath },
  when = true,
) => {
  const confirmDialog = useConfirmDialog();

  // This is to ensure that work is saved on page accidental exit or refresh
  useEffect(() => {
    if (when) {
      window.onbeforeunload = function () {
        return message;
      };
    }

    return () => {
      window.onbeforeunload = null;
    };
  }, [message, when]);

  const confirmExit = useCallback(
    async (confirmNavigation: () => void) => {
      const { confirmed } = await confirmDialog({
        title,
        message,
        onActionLabel,
      });
      if (confirmed) {
        confirmNavigation();
      }
    },
    [message, title, onActionLabel, confirmDialog],
  );

  useConfirmExit(confirmExit, when, baseExcludedPath);
};
